/**
 * The one sentence every screen uses for what comes back from a lifetime annuity.
 *
 * Owner, 25 Sep 2026: the tool had promised the purchase price back "at the end" on a capital-
 * returned annuity, on every surface, while assessing it at 60%. The Centrelink rules allow both
 * only inside the capital access schedule: the full amount during the first half of the owner's life
 * expectancy, then a straight line down to nothing (annuityStrategies.ts). Ten screens said it ten
 * ways; this says it once, in words a client can act on, and each screen reads it from here.
 */
import {
    getLifeExpectancy,
    capitalAccessPeriodYears,
    capitalAccessDeathBenefitFactor,
    capitalAccessWithdrawalFactor,
    sexOf,
    type Sex,
} from '../../../../shared/src/calculation/annuityStrategies.ts';

export interface CapitalAccessPeriod {
    periodYears: number;
    /** False when a sex was missing and the shorter table stood in. */
    sexKnown: boolean;
}

/** The schedule's period for an annuity on these lives — the longer of the two where the income continues to a partner. */
export function capitalAccessPeriodFor(input: {
    age: number | null | undefined;
    sex?: unknown;
    partnerAge?: number | null;
    partnerSex?: unknown;
    /** Joint ownership or a reversionary nomination: the schedule runs on the longer life. */
    joint?: boolean;
}): CapitalAccessPeriod | null {
    const age = typeof input.age === 'number' && Number.isFinite(input.age) ? input.age : null;
    if (age == null) return null;
    const sex = sexOf(input.sex);
    const partnerSex = sexOf(input.partnerSex);
    const joint = !!input.joint && typeof input.partnerAge === 'number' && Number.isFinite(input.partnerAge);
    const years = getLifeExpectancy({ age, partnerAge: joint ? (input.partnerAge as number) : null, type: joint ? 'joint' : 'single', sex, partnerSex });
    return { periodYears: capitalAccessPeriodYears(years), sexKnown: joint ? sex != null && partnerSex != null : sex != null };
}

const halfWords = (periodYears: number): string => {
    const half = periodYears / 2;
    return Number.isInteger(half) ? String(half) : `${Math.floor(half)}½`;
};

/**
 * What comes back, in plain words. `residualPct` is 0–100; `ownerName` reads as "you" by default.
 * With the period known the sentence carries the years; without it, the rule in general terms.
 */
export function capitalReturnedWords(input: {
    residualPct: number;
    periodYears?: number | null;
    sexKnown?: boolean | null;
    ownerName?: string | null;
    /** "the $500,000" — defaults to "the purchase price". */
    amountWords?: string | null;
}): string {
    const pct = Math.max(0, Math.min(100, Math.round(Number(input.residualPct) || 0)));
    const owner = (input.ownerName || 'you').trim() || 'you';
    const dies = owner === 'you' ? 'die' : 'dies';
    const possessive = owner === 'you' ? 'your' : `${owner}'s`;
    const amount = input.amountWords || 'the purchase price';
    if (pct <= 0) return `Nothing of ${amount} comes back on death: the provider keeps it, which is why the income is higher.`;
    const share = pct >= 100 ? `${amount.charAt(0).toUpperCase()}${amount.slice(1)}` : `${pct}% of ${amount}`;
    const period = input.periodYears != null && Number.isFinite(Number(input.periodYears)) && Number(input.periodYears) > 0 ? Number(input.periodYears) : null;
    const tail = input.sexKnown === false ? ' No sex is recorded on the profile, so the shorter life table is used.' : '';
    /*
     * TWO EDITS HERE, 25 Sep 2026, both from the owner reviewing the live screen.
     *
     * 1. THE TAPER NOW MATCHES THE MARKET. Past the halfway point the death benefit is not a fresh
     *    line of its own: it becomes the cash-out value, which has been falling since day one
     *    (capitalAccessDeathBenefitFactor). So the sentence says "whatever you could have cashed out"
     *    rather than "the amount falls each year" — one line for the client to follow, not two.
     * 2. THE CENTRELINK TAIL IS GONE. It used to end "...the most the Centrelink rules allow while
     *    still counting only 60% of the annuity." That fact is true, but this sentence appears on
     *    ten surfaces, every one of which also carries a "what this means for you" block stating the
     *    same 60%. Saying it in both places is what made the screen feel like it was repeating
     *    itself. The means test is now stated once, where the figures are.
     */
    if (period == null) {
        return `${share} comes back to the estate only if ${owner} ${dies} within the first half of ${possessive} life expectancy. After that the estate gets whatever could have been cashed out at the time, which falls to nothing by the end of that period.${tail}`;
    }
    return `${share} comes back to the estate only if ${owner} ${dies} within the first ${halfWords(period)} years. After that the estate gets whatever could have been cashed out at the time, falling to nothing after ${period} years.${tail}`;
}

/** The short form for a chip or a table cell. */
export function capitalReturnedShort(residualPct: number, periodYears?: number | null): string {
    const pct = Math.max(0, Math.min(100, Math.round(Number(residualPct) || 0)));
    if (pct <= 0) return 'nothing back on death';
    const period = periodYears != null && Number(periodYears) > 0 ? Number(periodYears) : null;
    return period
        ? `${pct}% back if death is within ${halfWords(period)} years, falling to nil by year ${period}`
        : `${pct}% back if death is early, falling to nil by life expectancy`;
}

export type { Sex };

/*
 * ---------------------------------------------------------------------------------------------
 * THE THREE QUESTIONS A CLIENT ACTUALLY ASKS
 *
 * Owner, 25 Sep 2026: "it's not just about having technically correct strategies, it's also about
 * how we show information to make it easy for the client to understand and follow."
 *
 * An annuity screen used to answer one question — what income do I get — and answered the other two
 * on separate screens, or not at all. A client deciding whether to commit a large sum is weighing
 * three things at once:
 *
 *      1. What do I get?              the income
 *      2. What does my family get?    the death benefit, on the capital access schedule
 *      3. What if I need it back?     the withdrawal value, which nothing used to show
 *
 * The third was not merely missing: two screens said the money was locked away and could never be
 * taken back, which is true only of a nil-residual annuity. On a capital-returned one there is a
 * declining withdrawal value for years. Everything below exists so that every screen answers all
 * three, in the same words, from one place.
 * ---------------------------------------------------------------------------------------------
 */

/**
 * How long the full-amount guarantee actually lasts, for THIS client, in their own years.
 *
 * WHY THIS EXISTS. The setup question said the full amount comes back "only if death is within the
 * first half of life expectancy". A client does not know their life expectancy, so the one clause
 * that decides their answer was the one they could not evaluate. Owner, 27 Sep 2026.
 *
 * IT DOES NOT SHOW A LIFE EXPECTANCY, deliberately. The owner ruled on 23 September that no
 * life-expectancy figure appears on a client screen, and that rule stands. "About the first 3 years"
 * is a term of the product; "your life expectancy is 6.5 years" is a statement about how long
 * someone will live. Only the first is said.
 *
 * AND IT NEVER SAYS THE WINDOW HAS RUN OUT, because it cannot. Life expectancy is measured from the
 * client's CURRENT age and resets every year they survive — a man of 92 still has close to four
 * years ahead of him, so a window of about eighteen months. There is no age at which the guarantee
 * becomes worthless, only ages at which it is short, which is the thing actually worth saying.
 *
 * FOR A COUPLE the window is uncertain at setup time: it runs on the longer of the two lives when
 * the income continues to the survivor, and whether it does is decided later, on the modelling
 * screen. So the shorter case is stated and the longer one flagged, never averaged.
 */
export function guaranteeWindowWords(input: {
    age: number | null | undefined;
    sex?: unknown;
    partnerAge?: number | null;
    partnerSex?: unknown;
    /** First names, for a sentence a client recognises. */
    ownerName?: string | null;
    partnerName?: string | null;
}): string | null {
    const own = capitalAccessPeriodFor({ age: input.age, sex: input.sex });
    if (!own) return null;

    const name = (input.ownerName || '').trim();
    const forWhom = name ? `for ${name}` : 'for you';
    const half = halfWords(own.periodYears);
    const base = `${forWhom} that is about the first ${half} ${own.periodYears === 2 ? 'year' : 'years'}`;

    const partnerAge = typeof input.partnerAge === 'number' && Number.isFinite(input.partnerAge) ? input.partnerAge : null;
    if (partnerAge == null) return base;

    // The joint window runs on whichever life is longer, so it is never shorter than the single one.
    const joint = capitalAccessPeriodFor({
        age: input.age,
        sex: input.sex,
        partnerAge,
        partnerSex: input.partnerSex,
        joint: true,
    });
    if (!joint || joint.periodYears <= own.periodYears) return base;

    const other = (input.partnerName || '').trim();
    return `${base}, and longer if the payments continue to ${other || 'the survivor'}`;
}

/** "3", "3½" — a schedule year as a client would read it. The halfway point is often a half year. */
export function scheduleYearLabel(yearsElapsed: number): string {
    const whole = Math.floor(yearsElapsed);
    return Math.abs(yearsElapsed - whole - 0.5) < 1e-9 ? `${whole}½` : String(whole);
}

/** One year of the capital access schedule: what the estate would get, and what the owner could take. */
export interface CapitalAccessYear {
    yearsElapsed: number;
    /** What the estate receives on death that year, in dollars. */
    onDeath: number;
    /** The most the rules let the owner take back that year. Usually more than a provider would pay. */
    ifCashedOut: number;
}

/**
 * The schedule year by year, for the small table the screens show.
 *
 * It returns the turning points rather than every year: the start, the half-way point where the
 * death benefit begins to fall, the year after it, and the year it reaches nothing. That is enough
 * for a client to see the shape without reading a twenty-row table.
 */
export function capitalAccessSchedule(input: {
    purchaseAmount: number;
    residualPct: number;
    periodYears: number | null | undefined;
}): CapitalAccessYear[] {
    const period = Number(input.periodYears);
    const amount = Number(input.purchaseAmount);
    const pct = Math.max(0, Math.min(100, Math.round(Number(input.residualPct) || 0)));
    if (!Number.isFinite(period) || period <= 0 || !Number.isFinite(amount) || amount <= 0 || pct <= 0) return [];

    const death = amount * (pct / 100);
    /*
     * THREE ROWS: the start, the moment the guarantee ends, and the end of the period. Now that the
     * death benefit becomes the cash-out value past the halfway point, a fourth row said nothing the
     * third did not.
     *
     * THE HALFWAY POINT IS NOT A WHOLE YEAR, and rounding it broke the table (owner-caught on the
     * live screen, 26 Sep 2026). On a seven-year period the guarantee runs to 3½ years; the row was
     * being rounded to year 3, which is still INSIDE the guarantee, so the table showed the full
     * amount at year 3 and nothing at year 7 with no sign of the step between. The one moment the
     * table exists to show was the one it skipped. The mark is now the exact half.
     */
    const half = period / 2;
    const seen = new Set<number>();
    const rows: CapitalAccessYear[] = [];
    for (const mark of [0, half, period]) {
        const year = Math.max(0, Math.min(period, mark));
        if (seen.has(year)) continue;
        seen.add(year);
        rows.push({
            yearsElapsed: year,
            onDeath: death * capitalAccessDeathBenefitFactor(year, period),
            // The withdrawal cap is a share of the PURCHASE PRICE, not of the death benefit: the two
            // caps in SIS reg 1.06A are separate lines, and only the death one is scaled by the
            // residual the client chose.
            ifCashedOut: amount * capitalAccessWithdrawalFactor(year, period),
        });
    }
    return rows.sort((a, b) => a.yearsElapsed - b.yearsElapsed);
}

/**
 * What the client could take back today, and whether there is anything to take.
 *
 * `null` means the shape has no withdrawal value at all, which is the honest answer for a
 * nil-residual annuity and the only case where "the money is locked away" is true.
 */
export function withdrawalValueNow(input: {
    purchaseAmount: number;
    residualPct: number;
    periodYears: number | null | undefined;
    yearsElapsed?: number;
}): number | null {
    const pct = Math.max(0, Math.min(100, Math.round(Number(input.residualPct) || 0)));
    const period = Number(input.periodYears);
    const amount = Number(input.purchaseAmount);
    if (pct <= 0) return null;
    if (!Number.isFinite(period) || period <= 0 || !Number.isFinite(amount) || amount <= 0) return null;
    return amount * capitalAccessWithdrawalFactor(Number(input.yearsElapsed) || 0, period);
}

/**
 * The sentence for "what if I need it back", in plain words.
 *
 * It always says the figure is a maximum and that cashing out ends the income, because both are
 * things a client would otherwise discover from the provider rather than from us.
 */
export function withdrawalWords(input: {
    residualPct: number;
    periodYears?: number | null;
    ownerName?: string | null;
}): string {
    const pct = Math.max(0, Math.min(100, Math.round(Number(input.residualPct) || 0)));
    const owner = (input.ownerName || 'you').trim() || 'you';
    const cannot = owner === 'you' ? 'you cannot' : `${owner} cannot`;
    if (pct <= 0) {
        return `There is no cash-out value on this shape: once it starts, ${cannot} take the money back. That is why it pays the most income.`;
    }
    const period = input.periodYears != null && Number(input.periodYears) > 0 ? Number(input.periodYears) : null;
    const window = period ? `falling to nothing after ${period} years` : 'falling each year to nothing by the end of the period';
    return `The money is not locked away. The most that can be taken back starts at the full amount and falls in a straight line, ${window}. What the provider actually pays depends on interest rates that day and the cost of breaking the investment, so it is usually less — and cashing out ends the income for good.`;
}

/**
 * The tooltip beside the residual control.
 *
 * IT USED TO BE ONE FIXED SENTENCE. At 0% it still told the client the money "comes back in full",
 * when nothing comes back at all — the one setting where the sentence was most wrong was the one it
 * never changed for. It now follows the control.
 */
export function residualTooltip(input: { residualPct: number; periodYears?: number | null }): string {
    const pct = Math.max(0, Math.min(100, Math.round(Number(input.residualPct) || 0)));
    const base = 'Sometimes called the residual capital value. The more you guarantee back, the lower your income.';
    if (pct <= 0) {
        return `${base} At nothing back, this pays the most of any shape: the provider keeps whatever is left, and there is no cash-out value either.`;
    }
    const period = input.periodYears != null && Number(input.periodYears) > 0 ? Number(input.periodYears) : null;
    const when = period
        ? `the first ${halfWords(period)} years. After that the estate gets whatever could have been cashed out at the time, and nothing after ${period} years`
        : 'the first half of life expectancy. After that the estate gets whatever could have been cashed out at the time, and nothing beyond that period';
    // No Centrelink sentence here: every screen that shows this control also shows a "what this
    // means for you" block with the 60% in it, and saying it twice is what made the page feel long.
    return `${base} The full amount is paid to the estate only if death comes within ${when}.`;
}

/**
 * The label on the residual control.
 *
 * "Leave some money to your family at the end?" read as "at the end of your life", which is the one
 * thing it does not mean. For a client in residential care, whose worry is living a long time, the
 * old label appeared to answer that worry and in fact answered the opposite. The condition now sits
 * in the label itself.
 */
export function residualQuestion(input: { periodYears?: number | null; ownerName?: string | null }): string {
    const owner = (input.ownerName || 'you').trim() || 'you';
    const dies = owner === 'you' ? 'die' : 'dies';
    const period = input.periodYears != null && Number(input.periodYears) > 0 ? Number(input.periodYears) : null;
    if (period == null) return `If ${owner} ${dies} early, how much comes back?`;
    return `If ${owner} ${dies} within the first ${halfWords(period)} years, how much comes back?`;
}
